import React from "react";
import { NestedContentSliceFragment } from "../../types/graphql";
import {
  NestedContentBase,
  TitleWrapper,
  Title,
  TitleLink,
  NestedContentContainer,
} from "./nested-content.styled";
import Entry from "./nested-content-entry";
import { helpCenterURLBase } from "../../utils/constants";
import { ConnectorProvided } from "react-instantsearch-core";
import { qsObjectToSearchState } from "../search/query-string";
import { SiteSearchResults, helpCenterTabName } from "../search/config";
import FAQMetadata from "./faq-meta-data";
import { SharedSliceType } from "../shared-slice/shared-slice";
import { SliceComponentProps } from "@prismicio/react";
import { Slice } from "@prismicio/types";
import { IPageContext } from "../../types/types";

export type INestedContentProps = Slice<
  SharedSliceType,
  NestedContentSliceFragment["primary"],
  NestedContentSliceFragment["items"][number]
> & { type: SharedSliceType };

type NestedContentContext = Omit<
  IPageContext,
  "lang" | "type" | "tags" | "prismicId" | "showHeadingAnchors"
> & {
  helpCenterNestedContent?: {
    title: string;
    refine: ConnectorProvided<{}>["refine"];
    searchData: SiteSearchResults;
    handleTabChangeOnRefine: (nextTabName: string) => void;
  };
};

const NestedContent = ({
  slice,
  context,
}: SliceComponentProps<INestedContentProps, NestedContentContext>) => {
  const prismicContentFields = slice.items;
  const title = slice.primary?.title || context.helpCenterNestedContent?.title;
  const isFAQ = slice.primary?.is_faq;
  const helpCenterSearchData = context.helpCenterNestedContent?.searchData;
  const helpCenterContentFields =
    context.helpCenterNestedContent?.searchData.hits;
  const contentLayout = slice.primary?.content_layout || "default";
  const width = slice.primary?.width || "narrow";
  const expandFirstItem = slice.primary?.expand_first_item || false;
  const titleAlignment = slice.primary?.title_alignment || "left-align-text";

  return prismicContentFields || helpCenterContentFields ? (
    <NestedContentBase className="slice-section">
      <NestedContentContainer className={`container ${width}`}>
        {title && (
          <TitleWrapper className={titleAlignment}>
            <Title>{title}</Title>
            {helpCenterContentFields && (
              <TitleLink
                to={`/search/?${
                  helpCenterSearchData?.query &&
                  `query=${helpCenterSearchData.query}&`
                }index=${helpCenterTabName}`}
                onClick={(e) => {
                  e.preventDefault();
                  context.helpCenterNestedContent?.handleTabChangeOnRefine(
                    helpCenterTabName
                  );
                  const searchState = qsObjectToSearchState({
                    query: helpCenterSearchData?.query,
                    index: helpCenterTabName,
                  });
                  context.helpCenterNestedContent?.refine(searchState);
                }}
              >
                View All
              </TitleLink>
            )}
          </TitleWrapper>
        )}
        {prismicContentFields && isFAQ && (
          <FAQMetadata nestedContent={prismicContentFields} />
        )}
        {(prismicContentFields &&
          prismicContentFields?.map(
            (entry, index) =>
              entry &&
              entry.header &&
              entry.content && (
                <Entry
                  key={index}
                  header={entry.header}
                  prismicContent={entry.content}
                  contentLayout={contentLayout}
                  expanded={index === 0 && expandFirstItem}
                />
              )
          )) ||
          (helpCenterContentFields &&
            helpCenterContentFields.map(
              (entry, index) =>
                entry &&
                entry.title &&
                entry.body_safe &&
                helpCenterSearchData && (
                  <Entry
                    key={entry.id}
                    header={entry.title}
                    helpCenterContent={{
                      entryText: entry.body_safe,
                      entryURL: `${helpCenterURLBase}${entry.id}`,
                      hit: entry,
                      hitIndex: index,
                      searchData: helpCenterSearchData,
                    }}
                    contentLayout={contentLayout}
                    expanded={index === 0 && expandFirstItem}
                  />
                )
            ))}
      </NestedContentContainer>
    </NestedContentBase>
  ) : (
    <></>
  );
};

export default NestedContent;
